import React from 'react';
import { Empty, Skeleton, Spin } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import ConciergeBellIcon from './ConciergeBellIcon';
import { ReceptionistCallsDayGroup } from '../types/ReceptionistCallDisplay';

interface ReceptionistCallListProps {
  groups: ReceptionistCallsDayGroup[];
  callsCount: number;
  selectedCallId: string | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  onSelect: (callId: string) => void;
}

const ReceptionistCallList: React.FC<ReceptionistCallListProps> = ({ groups, callsCount, selectedCallId, isLoading, isLoadingMore, onSelect }) => {
  return (
    <div className="receptionist-calls__list">
      <div className="receptionist-calls__list-header">
        <span className="receptionist-calls__list-count">
          {callsCount} appel{callsCount > 1 ? 's' : ''}
          {isLoadingMore && <Spin size="small" className="receptionist-calls__list-more" />}
        </span>
        <span className="receptionist-calls__list-sort">Plus récents d'abord</span>
      </div>

      <div className="receptionist-calls__list-scroll">
        {/* Nothing matches yet but pages are still landing: not an empty period */}
        {isLoading || (groups.length === 0 && isLoadingMore) ? (
          <div className="receptionist-calls__list-loading">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : groups.length === 0 ? (
          <Empty description="Aucun appel sur cette période" className="receptionist-calls__empty" />
        ) : (
          groups.map((group) => (
            <div key={group.key}>
              <div className="receptionist-calls__day">{group.label}</div>
              {group.calls.map((call) => (
                <button
                  key={call.id}
                  type="button"
                  className={classNames('receptionist-calls__item', {
                    'receptionist-calls__item--active': call.id === selectedCallId,
                    'receptionist-calls__item--urgent': call.isUrgent,
                    'receptionist-calls__item--unread': call.isUnread && call.isOwn,
                  })}
                  onClick={() => onSelect(call.id)}
                >
                  <div className="receptionist-calls__item-head">
                    <span className="receptionist-calls__item-name">
                      {call.isUnread && call.isOwn ? (
                        <span className="receptionist-calls__unread-dot" />
                      ) : (
                        call.isUrgent && <span className="receptionist-calls__urgent-dot" />
                      )}
                      {call.callerName}
                    </span>
                    <span className="receptionist-calls__item-time">{dayjs(call.created).format('HH:mm')}</span>
                  </div>
                  {(call.preview || call.title) && (
                    <div className="receptionist-calls__item-preview">{call.preview || call.title}</div>
                  )}
                  <div className="receptionist-calls__item-owner">
                    <ConciergeBellIcon />
                    <span>
                      {call.assistantName}
                      {call.userName && ` · ${call.userName.split(' ')[0]}`}
                    </span>
                  </div>
                  <div className="receptionist-calls__item-tags">
                    {call.motifLabel && <span className="receptionist-calls__tag receptionist-calls__tag--motif">{call.motifLabel}</span>}
                    {call.isUrgent && <span className="receptionist-calls__tag receptionist-calls__tag--urgent">Urgent</span>}
                    {!call.hasContent && <span className="receptionist-calls__tag receptionist-calls__tag--muted">Sans contenu</span>}
                  </div>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReceptionistCallList;
